import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { DiscountRuleApiService } from '../services/discount-rule-api.service';
import {
  DiscountRuleRequest,
  DiscountRuleResponse,
} from '../models/discount-rule.model';
import { ProductoApiService } from '../services/producto-api.service';
import { ProductResponse } from '../models/producto.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';
import { Contact } from '../../contactos/models/contacto.model';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';

@Component({
  selector: 'app-discount-rule-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
  ],
  templateUrl: './discount-rule-form.component.html',
})
export class DiscountRuleFormComponent implements OnInit {
  @Input() drawerMode = false;
  @Output() saved = new EventEmitter<DiscountRuleResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  ruleId: number | null = null;

  productSearch = '';
  productResults: ProductResponse[] = [];
  searchingProducts = false;
  selectedProduct: ProductResponse | null = null;

  private businessId!: number;

  minDatetimeLocal = '';

  contactSearchTerm = '';
  contactSearchResults: Contact[] = [];
  selectedContact: Contact | null = null;
  showContactDropdown = false;
  searchingContacts = false;
  loadingMoreContacts = false;

  constructor(
    private fb: FormBuilder,
    private ruleApi: DiscountRuleApiService,
    private productApi: ProductoApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
    private contactApi: ContactoApiService,
  ) {}

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    this.businessId = user?.businessId || 1;
    this.updateMinDate();
    this.initForm();

    if (!this.drawerMode) {
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.isEditing = true;
        this.ruleId = +id;
        this.cargarRegla(this.ruleId);
      }
    }
  }

  reset(): void {
    this.isEditing = false;
    this.ruleId = null;
    this.error = '';
    this.saving = false;
    this.selectedProduct = null;
    this.productSearch = '';
    this.productResults = [];
    this.initForm();
  }

  loadDiscountRule(id: number): void {
    this.isEditing = true;
    this.ruleId = id;
    this.cargarRegla(id);
  }

  private initForm(): void {
    this.form = this.fb.group(
      {
        ruleType: ['volume', Validators.required],
        discountType: ['percent'],
        discountValue: [null, Validators.min(0)],
        minQuantity: [null, Validators.min(0)],
        maxQuantity: [null, Validators.min(0)],
        contactId: [null],
        couponCode: ['', Validators.maxLength(50)],
        maxUses: [null, Validators.min(1)],
        minSalePrice: [null, Validators.min(0)],
        validFrom: [null],
        validUntil: [null],
        productId: [null, Validators.required],
        description: [''],
      },
      {
        validators: [this.dateRangeValidator, this.couponCodeValidator],
      },
    );
  }

  private couponCodeValidator: ValidatorFn = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const ruleType = group.get('ruleType')?.value;
    const couponCode = group.get('couponCode')?.value;

    if (ruleType === 'coupon' && !couponCode?.trim()) {
      group.get('couponCode')?.setErrors({ required: true });
    } else {
      const errors = group.get('couponCode')?.errors;
      if (errors?.['required']) {
        delete errors['required'];
        group
          .get('couponCode')
          ?.setErrors(Object.keys(errors).length ? errors : null);
      }
    }

    return null;
  };

  private updateMinDate(): void {
    this.minDatetimeLocal = this.toLocalDateInput(new Date());
  }

  private toLocalDateInput(d: Date): string {
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private dateRangeValidator(group: AbstractControl): ValidationErrors | null {
    const from = group.get('validFrom')?.value;
    const until = group.get('validUntil')?.value;

    if (!from || !until) return null;

    const fromDate = new Date(from);
    const untilDate = new Date(until);

    if (untilDate < fromDate) {
      return { dateRangeInvalid: true };
    }

    return null;
  }

  private cargarContactoSeleccionado(contactId: number): void {
    this.contactApi.getContactById(contactId).subscribe({
      next: (contact) => {
        this.selectedContact = contact;

        this.contactSearchTerm = `${contact.fullname}${contact.lastname ? ' ' + contact.lastname : ''}`;
      },
      error: () => {
        this.contactSearchTerm = `Contacto #${contactId}`;
      },
    });
  }

  private cargarRegla(id: number): void {
    this.loading = true;
    this.ruleApi.getDiscountRuleById(id).subscribe({
      next: (rule) => {
        this.form.patchValue({
          ruleType: rule.ruleType,
          discountType: rule.discountType || 'percent',
          discountValue: rule.discountValue ?? null,
          minQuantity: rule.minQuantity ?? null,
          maxQuantity: rule.maxQuantity ?? null,
          contactId: rule.contactId ?? null,
          couponCode: rule.couponCode || '',
          maxUses: rule.maxUses ?? null,
          minSalePrice: rule.minSalePrice ?? null,
          validFrom: rule.validFrom || null,
          validUntil: rule.validUntil || null,
          productId: rule.productId ?? null,
          description: rule.description || '',
        });
        if (rule.productName) {
          this.productSearch = rule.productName;
        } else if (rule.productId) {
          this.cargarProductoSeleccionado(rule.productId);
        }
        if (rule.contactId) {
          this.cargarContactoSeleccionado(rule.contactId);
        }

        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la regla';
        this.loading = false;
      },
    });
  }

  get pageTitle(): string {
    return this.isEditing
      ? 'Editar Regla de Descuento'
      : 'Nueva Regla de Descuento';
  }

  get ruleType(): string {
    return this.form.get('ruleType')?.value;
  }

  get hasDateRangeError(): boolean {
    const validFromTouched = this.form.get('validFrom')?.touched;
    const validUntilTouched = this.form.get('validUntil')?.touched;
    return (
      this.form.errors?.['dateRangeInvalid'] === true &&
      (validFromTouched || validUntilTouched) === true
    );
  }

  buscarProductos(): void {
    if (this.productSearch.length < 2) {
      this.productResults = [];
      return;
    }
    this.searchingProducts = true;
    this.productApi
      .searchProduct({
        businessId: this.businessId,
        searchName: this.productSearch,
        page: 0,
        size: 10,
      })
      .subscribe({
        next: (res) => {
          this.productResults = res.content;
          this.searchingProducts = false;
        },
        error: () => {
          this.productResults = [];
          this.searchingProducts = false;
        },
      });
  }

  onFocusProducto(): void {
    if (this.productSearch.length >= 2) return;
    this.searchingProducts = true;
    this.productApi
      .searchProduct({
        businessId: this.businessId,
        page: 0,
        size: 10,
      })
      .subscribe({
        next: (res) => {
          this.productResults = res.content;
          this.searchingProducts = false;
        },
        error: () => {
          this.productResults = [];
          this.searchingProducts = false;
        },
      });
  }

  onBlurProducto(): void {
    setTimeout(() => {
      this.productResults = [];
    }, 200);
  }

  seleccionarProducto(product: ProductResponse): void {
    this.selectedProduct = product;
    this.form.patchValue({ productId: product.id });
    this.form.markAsDirty();
    this.form.get('productId')?.markAsTouched();
    this.form.get('productId')?.updateValueAndValidity();
    this.productSearch = product.name;
    this.productResults = [];
  }

  limpiarProducto(): void {
    this.selectedProduct = null;
    this.form.patchValue({ productId: null });
    this.form.markAsDirty();
    this.form.get('productId')?.markAsTouched();
    this.form.get('productId')?.updateValueAndValidity();
    this.productSearch = '';
  }

  private cargarProductoSeleccionado(productId: number): void {
    this.productApi.getProductById(productId, this.businessId).subscribe({
      next: (product) => {
        this.selectedProduct = product;
        this.productSearch = product.name;
      },
      error: () => {
        this.productSearch = `Producto #${productId}`;
      },
    });
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.form.pristine) {
      this.toast.info({
        title: 'Regla de descuento',
        message: 'No se realizaron nuevos cambios en la regla de descuento',
      });
      this.router.navigate(['/productos/reglas-descuento']);
      return;
    }

    this.saving = true;
    this.error = '';

    const v = this.form.value;
    const request: DiscountRuleRequest = {
      ruleType: v.ruleType,
      businessId: this.businessId,
      productId: v.productId || undefined,
      contactId: v.contactId || undefined,
      minQuantity: v.minQuantity ?? undefined,
      maxQuantity: v.maxQuantity ?? undefined,
      discountType: v.ruleType === 'min_price' ? undefined : v.discountType,
      discountValue: v.discountValue ?? undefined,
      couponCode: v.couponCode || undefined,
      maxUses: v.maxUses ?? undefined,
      minSalePrice: v.minSalePrice ?? undefined,
      validFrom: v.validFrom || undefined,
      validUntil: v.validUntil || undefined,
      description: v.description || undefined,
    };

    const request$ =
      this.isEditing && this.ruleId
        ? this.ruleApi.updateDiscountRule(this.ruleId, request)
        : this.ruleApi.createDiscountRule(request);

    const wasEditing = this.isEditing;
    request$.subscribe({
      next: (rule) => {
        this.saving = false;
        if (wasEditing) this.toast.updated('Regla de descuento');
        else this.toast.created('Regla de descuento');
        if (this.drawerMode) {
          this.saved.emit(rule);
        } else {
          this.router.navigate(['/productos/reglas-descuento']);
        }
      },
      error: (err) => {
        this.saving = false;
        // Inc 39: muestra el mensaje del backend (p. ej. 400 de fechas de vigencia).
        this.error =
          err.error?.message ||
          (wasEditing ? 'Error al actualizar' : 'Error al crear');
        this.toast.error({ title: 'Error', message: this.error });
      },
    });
  }

  cancelar(): void {
    if (this.drawerMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/productos/reglas-descuento']);
    }
  }

  hasError(field: string): boolean {
    const control = this.form.get(field);
    return control ? control.invalid && control.touched : false;
  }

  getError(field: string): string {
    const control = this.form.get(field);
    if (!control || !control.errors) return '';
    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['min'])
      return `El valor mínimo es ${control.errors['min'].min}`;
    return 'Campo inválido';
  }

  onContactSearchInput(value: string): void {
    this.contactSearchTerm = value;

    if (value.trim().length < 2) {
      this.contactSearchResults = [];
      this.showContactDropdown = false;
      return;
    }

    this.searchingContacts = true;

    this.contactApi
      .searchContacts({
        businessId: this.businessId,
        searchTerm: value.trim(),
        page: 0,
        size: 10,
      })
      .subscribe({
        next: (response) => {
          this.contactSearchResults = response.content;
          this.showContactDropdown = this.contactSearchResults.length > 0;
          this.searchingContacts = false;
        },
        error: () => {
          this.contactSearchResults = [];
          this.showContactDropdown = false;
          this.searchingContacts = false;
        },
      });
  }
  hideDropdownDelayed(): void {
    setTimeout(() => {
      this.showContactDropdown = false;
    }, 200);
  }

  selectContact(contact: Contact): void {
    this.selectedContact = contact;
    this.form.patchValue({
      contactId: contact.id,
    });

    this.contactSearchTerm = `${contact.fullname}${contact.lastname ? ' ' + contact.lastname : ''}`;

    this.contactSearchResults = [];
    this.showContactDropdown = false;
    this.form.markAsDirty();
  }

  clearSelectedContact(): void {
    this.selectedContact = null;

    this.contactSearchTerm = '';
    this.contactSearchResults = [];
    this.showContactDropdown = false;

    this.form.patchValue({
      contactId: null,
    });

    this.form.markAsDirty();
  }

  getContactInitials(contact: Contact): string {
    const first = contact.fullname
      ? contact.fullname.charAt(0).toUpperCase()
      : '';

    const last = contact.lastname
      ? contact.lastname.charAt(0).toUpperCase()
      : contact.fullname.length > 1
        ? contact.fullname.charAt(1).toUpperCase()
        : '';

    return first + last;
  }

  getContactAvatarColor(contact: Contact): string {
    const colors = [
      'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400',
      'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
      'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
      'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    ];

    const name = contact.fullname || '';

    const charSum = name
      .split('')
      .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);

    return colors[charSum % colors.length];
  }

  onContactSearchEnter(event: Event): void {
    event.preventDefault();

    this.searchingContacts = true;
    this.showContactDropdown = false;

    this.contactApi
      .searchContacts({
        businessId: this.businessId,
        searchTerm: this.contactSearchTerm.trim(), // puede ser ''
        page: 0,
        size: 10,
      })
      .subscribe({
        next: (response) => {
          this.contactSearchResults = response.content;
          this.showContactDropdown = this.contactSearchResults.length > 0;
          this.searchingContacts = false;
        },
        error: () => {
          this.contactSearchResults = [];
          this.showContactDropdown = false;
          this.searchingContacts = false;
        },
      });
  }
}
